<script lang="ts">
	import { yyyy } from "./store.svelte";
	import Fa from "svelte-fa";
	import {
		faUpload,
		faCircleNotch,
		faCheck,
		faChevronLeft,
	} from "@fortawesome/free-solid-svg-icons";

	const url = yyyy.uri.api;

	interface Props {
		onComplete: () => void;
		onCancel: () => void;
	}
	let { onComplete, onCancel }: Props = $props();

	// Steps: file → inspect → layers → saving
	let step = $state<"file" | "uploading" | "inspecting" | "layers" | "saving">("file");

	// Upload state
	let file_upload: HTMLInputElement;
	let uploadProgress = $state(0);
	let filename = $state("");
	let dragOver = $state(false);
	let error = $state("");

	// Inspect result
	let inspectionId = $state("");
	let layers = $state<any[]>([]);
	let selectedLayers = $state<Set<string>>(new Set());

	// Per-layer metadata (keyed by layer name)
	let layerMeta = $state<Record<string, { name: string; description: string; category: string; source: string }>>({});

	const categories = [
		"administrative",
		"feeder",
		"regulatory",
		"environmental",
		"development",
		"other",
	];

	const toggleLayer = (layerName: string) => {
		const next = new Set(selectedLayers);
		if (next.has(layerName)) {
			next.delete(layerName);
		} else {
			next.add(layerName);
		}
		selectedLayers = next;
	};

	const toggleAll = () => {
		if (selectedLayers.size === layers.length) {
			selectedLayers = new Set();
		} else {
			selectedLayers = new Set(layers.map((l: any) => l.layer));
		}
	};

	const ensureMeta = (layerName: string) => {
		if (!layerMeta[layerName]) {
			layerMeta[layerName] = {
				name: layerName.replace(/[_-]/g, " "),
				description: "",
				category: "",
				source: "",
			};
		}
	};

	// --- File upload ---

	const getSignedRequest = async (file: File) => {
		const response = await fetch(`${url}/rpc/scope_get_url_for_upload`, {
			method: "post",
			body: JSON.stringify({ filename: file.name, filetype: file.type || "application/octet-stream" }),
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + yyyy.sesh.logtoken.token,
			},
		});

		if (response.ok) {
			const data = await response.json();
			uploadFile(file, data);
		} else {
			error = "Failed to get upload URL";
			step = "file";
		}
	};

	const fileKickOff = () => {
		if (!file_upload.files?.length) return;
		startUpload(file_upload.files[0]);
	};

	const startUpload = (file: File) => {
		error = "";
		uploadProgress = 0.001;
		step = "uploading";
		filename = file.name;
		getSignedRequest(file);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		dragOver = false;
		if (e.dataTransfer?.files?.length) {
			startUpload(e.dataTransfer.files[0]);
		}
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		dragOver = true;
	};

	const handleDragLeave = () => {
		dragOver = false;
	};

	const uploadFile = (file: File, s3Data: any) => {
		const postData = new FormData();
		for (let key in s3Data.data.fields) {
			postData.append(key, s3Data.data.fields[key]);
		}
		postData.append("file", file);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", s3Data.url);

		xhr.onreadystatechange = () => {
			if (xhr.readyState === 4) {
				if (xhr.status === 200 || xhr.status === 204) {
					inspectFile(s3Data.url);
				} else {
					error = "File could not be uploaded";
					step = "file";
				}
			}
		};

		xhr.upload.addEventListener("progress", (e) => {
			uploadProgress = e.loaded / e.total;
		});

		xhr.send(postData);
	};

	// --- Inspect ---

	const inspectFile = async (fileUrl: string) => {
		step = "inspecting";
		error = "";

		try {
			const response = await fetch(`${url}/rpc/scope_inspect`, {
				method: "post",
				body: JSON.stringify({ file_url: fileUrl }),
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
			});

			if (!response.ok) {
				const data = await response.json();
				throw new Error(data.message || "Failed to inspect file");
			}

			const result = await response.json();
			inspectionId = result.inspection_id;
			layers = result.layers;

			// Auto-select all layers and init metadata
			selectedLayers = new Set(layers.map((l: any) => l.layer));
			for (const l of layers) {
				ensureMeta(l.layer);
			}

			// If single layer, pre-fill name from filename
			if (layers.length === 1) {
				layerMeta[layers[0].layer].name = filename.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
			}

			step = "layers";
		} catch (err: any) {
			error = err.message;
			step = "file";
		}
	};

	// --- Materialize ---

	const materialize = async () => {
		if (selectedLayers.size === 0) return;
		step = "saving";
		error = "";

		const layerPayload = Array.from(selectedLayers).map((layerName) => {
			ensureMeta(layerName);
			const meta = layerMeta[layerName];
			return {
				layer: layerName,
				name: meta.name.trim() || layerName,
				description: meta.description.trim() || null,
				category: meta.category || null,
				source: meta.source.trim() || null,
			};
		});

		try {
			const response = await fetch(`${url}/rpc/scope_materialize`, {
				method: "post",
				body: JSON.stringify({
					inspection_id: inspectionId,
					layers: layerPayload,
				}),
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
			});

			if (!response.ok) {
				const data = await response.json();
				throw new Error(data.message || "Failed to save scopes");
			}

			onComplete();
		} catch (err: any) {
			error = err.message;
			step = "layers";
		}
	};
</script>

<div class="scope-upload">
	<h3 class="upload-title">
		{#if step === "file" || step === "uploading"}
			Upload Scope
		{:else if step === "inspecting"}
			Inspecting File...
		{:else if step === "layers"}
			Select Layers
		{:else}
			Saving Scopes...
		{/if}
	</h3>

	{#if step === "file"}
		<p class="upload-hint">
			Supported: GeoJSON, zipped Shapefile, KML/KMZ, GeoPackage
		</p>

		<button
			type="button"
			class="upload-btn"
			class:drag-over={dragOver}
			onclick={() => file_upload.click()}
			ondrop={handleDrop}
			ondragover={handleDragOver}
			ondragleave={handleDragLeave}
		>
			<Fa icon={faUpload} size="2x" />
			<span>Drag & drop your file here</span>
			<span class="upload-or">or click to browse</span>
		</button>
		<input
			bind:this={file_upload}
			type="file"
			class="hidden-input"
			accept=".geojson,.json,.zip,.kml,.kmz,.gpkg"
			onchange={fileKickOff}
		/>

	{:else if step === "uploading"}
		<div class="upload-progress">
			<h2>{Math.round(uploadProgress * 100)}%</h2>
			<progress class="progress is-primary" value={uploadProgress} max="1"></progress>
			<p class="filename">{filename}</p>
		</div>

	{:else if step === "inspecting"}
		<div class="inspecting-state">
			<Fa icon={faCircleNotch} spin size="2x" />
			<p>Reading layers from <strong>{filename}</strong>...</p>
			<p class="upload-hint">This may take a moment for large files</p>
		</div>

	{:else if step === "layers"}
		<div class="layers-step">
		<p class="upload-hint">
			Found {layers.length} layer{layers.length !== 1 ? "s" : ""} in <strong>{filename}</strong>
		</p>

		<div class="layer-list">
			{#if layers.length > 1}
				<div class="layer-toggle-all">
					<label class="checkbox-label">
						<input
							type="checkbox"
							checked={selectedLayers.size === layers.length}
							onchange={toggleAll}
						/>
						<span>Select all</span>
					</label>
				</div>
			{/if}

			{#each layers as layer (layer.layer)}
				<div class="layer-item" class:selected={selectedLayers.has(layer.layer)}>
					<div class="layer-header">
						<label class="checkbox-label">
							<input
								type="checkbox"
								checked={selectedLayers.has(layer.layer)}
								onchange={() => toggleLayer(layer.layer)}
							/>
							<span class="layer-name">{layer.layer}</span>
						</label>
						<span class="layer-info">
							{layer.feature_count} features
							{#if layer.geom_type}
								&middot; {layer.geom_type.replace("ST_", "")}
							{/if}
						</span>
					</div>

					{#if selectedLayers.has(layer.layer) && layerMeta[layer.layer]}
						<div class="layer-meta">
							<div class="field">
								<label class="label" for="name-{layer.layer}">Scope name</label>
								<input
									id="name-{layer.layer}"
									class="input"
									type="text"
									placeholder="Name this scope"
									bind:value={layerMeta[layer.layer].name}
								/>
							</div>
							<div class="field">
								<label class="label" for="desc-{layer.layer}">Description</label>
								<input
									id="desc-{layer.layer}"
									class="input"
									type="text"
									placeholder="Optional"
									bind:value={layerMeta[layer.layer].description}
								/>
							</div>
							<div class="field-row">
								<div class="field">
									<label class="label" for="cat-{layer.layer}">Category</label>
									<div class="select is-fullwidth">
										<select id="cat-{layer.layer}" bind:value={layerMeta[layer.layer].category}>
											<option value="">Select...</option>
											{#each categories as cat}
												<option value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
											{/each}
										</select>
									</div>
								</div>
								<div class="field">
									<label class="label" for="src-{layer.layer}">Source</label>
									<input
										id="src-{layer.layer}"
										class="input"
										type="text"
										placeholder="e.g. National Land Agency"
										bind:value={layerMeta[layer.layer].source}
									/>
								</div>
							</div>
							{#if layer.columns && layer.columns.length > 0}
								<p class="columns-hint">
									Columns: {layer.columns.join(", ")}
								</p>
							{/if}
						</div>
					{/if}
				</div>
			{/each}
		</div>

		<div class="layer-actions">
			<button
				class="button lvwr-toggle-orange is-fullwidth"
				onclick={materialize}
				disabled={selectedLayers.size === 0}
			>
				<Fa icon={faCheck} />
				<span style="margin-left: 0.4rem;">
					Save {selectedLayers.size} Scope{selectedLayers.size !== 1 ? "s" : ""}
				</span>
			</button>
		</div>
		</div>

	{:else if step === "saving"}
		<div class="inspecting-state">
			<Fa icon={faCircleNotch} spin size="2x" />
			<p>Materializing {selectedLayers.size} scope{selectedLayers.size !== 1 ? "s" : ""}...</p>
		</div>
	{/if}

	{#if error}
		<p class="error-msg">{error}</p>
	{/if}

	{#if step !== "saving" && step !== "inspecting"}
		<button class="button cancel-btn" onclick={onCancel}>
			{#if step === "layers"}
				<Fa icon={faChevronLeft} />
				<span style="margin-left: 0.3rem;">Cancel</span>
			{:else}
				Cancel
			{/if}
		</button>
	{/if}
</div>

<style>
	.scope-upload {
		padding: 1rem 0;
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		overflow-y: auto;
	}

	.upload-title {
		font-size: 1rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 0.75rem;
	}

	.upload-hint {
		color: #666;
		font-size: 0.85rem;
		margin-bottom: 1rem;
	}

	.upload-btn {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.5rem;
		padding: 2rem;
		width: 100%;
		background: #f5f5f5;
		border: 2px dashed #ccc;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s;
		color: #666;
	}

	.upload-btn:hover,
	.upload-btn.drag-over {
		background: #fff3e0;
		border-color: #ff3e00;
		color: #ff3e00;
	}

	.upload-or {
		font-size: 0.8rem;
		opacity: 0.7;
	}

	.hidden-input {
		display: none;
	}

	.upload-progress {
		text-align: center;
		padding: 1rem 0;
	}

	.upload-progress h2 {
		font-size: 2.5rem;
		font-weight: 200;
		color: #ff3e00;
		margin-bottom: 0.5rem;
	}

	.upload-progress .progress {
		height: 6px;
		margin-bottom: 0.5rem;
	}

	.filename {
		color: #666;
		font-size: 0.85rem;
	}

	.inspecting-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #666;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.layers-step {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		overflow: hidden;
	}

	/* Layer list */
	.layer-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex: 1;
		min-height: 0;
		overflow-y: auto;
	}

	.layer-toggle-all {
		padding: 6px 0;
		border-bottom: 1px solid #eee;
		margin-bottom: 4px;
	}

	.layer-item {
		border: 1px solid #dbdbdb;
		border-radius: 6px;
		overflow: hidden;
		transition: all 0.2s;
		flex-shrink: 0;
	}

	.layer-item.selected {
		border-color: #ff3e00;
		background: rgba(255, 62, 0, 0.02);
	}

	.layer-header {
		padding: 8px 10px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		font-size: 0.85rem;
	}

	.checkbox-label {
		display: flex;
		align-items: center;
		gap: 8px;
		cursor: pointer;
	}

	.layer-name {
		font-weight: 500;
		color: #363636;
	}

	.layer-info {
		color: #999;
		font-size: 0.75rem;
		white-space: nowrap;
	}

	.layer-meta {
		padding: 8px 10px;
		border-top: 1px solid #eee;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.field-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.75rem;
	}

	.label {
		font-weight: 600;
		font-size: 0.75rem;
		margin-bottom: 0.2rem;
	}

	.columns-hint {
		font-size: 0.7rem;
		color: #999;
		font-style: italic;
		margin-top: 2px;
	}

	.layer-actions {
		margin-top: 1rem;
	}

	.cancel-btn {
		margin-top: 0.75rem;
		width: 100%;
	}

	.error-msg {
		color: #ff3860;
		font-size: 0.85rem;
		margin-top: 0.5rem;
	}
</style>
